export const PERIOD_COLUMN_WIDGET_TYPE = 'period-column';

export const PERIOD_COLUMN_PERIODS = Object.freeze({
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
});

export const isPeriodColumnWidgetType = (value) =>
  String(value || '').trim() === PERIOD_COLUMN_WIDGET_TYPE;

export const normalizePeriodColumnPeriod = (value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();

  // Keep legacy "year" widgets functional by treating them as monthly widgets.
  if (normalizedValue === 'year') {
    return PERIOD_COLUMN_PERIODS.MONTH;
  }

  return [PERIOD_COLUMN_PERIODS.DAY, PERIOD_COLUMN_PERIODS.WEEK, PERIOD_COLUMN_PERIODS.MONTH]
    .includes(normalizedValue)
    ? normalizedValue
    : PERIOD_COLUMN_PERIODS.DAY;
};

export const normalizePeriodColumnStat = (value) =>
  String(value || '').trim().toLowerCase() === 'min' ? 'min' : 'max';

const toUnixSeconds = (value) => Math.floor(value.getTime() / 1000);

const addUtcDays = (value, amount) => {
  const nextValue = new Date(value.getTime());
  nextValue.setUTCDate(nextValue.getUTCDate() + amount);
  return nextValue;
};

const addUtcMonths = (value, amount) => new Date(Date.UTC(
  value.getUTCFullYear(),
  value.getUTCMonth() + amount,
  1,
  0,
  0,
  0,
  0,
));

const startOfUtcDay = (value) => new Date(Date.UTC(
  value.getUTCFullYear(),
  value.getUTCMonth(),
  value.getUTCDate(),
  0,
  0,
  0,
  0,
));

const startOfUtcMonth = (value) => new Date(Date.UTC(
  value.getUTCFullYear(),
  value.getUTCMonth(),
  1,
  0,
  0,
  0,
  0,
));

const startOfUtcWeekOfMonth = (value) => {
  const date = new Date(Date.UTC(
    value.getUTCFullYear(),
    value.getUTCMonth(),
    value.getUTCDate(),
    0,
    0,
    0,
    0,
  ));
  const dayOfMonth = date.getUTCDate();
  const weekStartDay = (Math.floor((dayOfMonth - 1) / 7) * 7) + 1;
  date.setUTCDate(weekStartDay);
  return date;
};

const getExpectedPeriodBucketStarts = (period, nowInput = new Date()) => {
  const normalizedPeriod = normalizePeriodColumnPeriod(period);
  const now = nowInput instanceof Date ? new Date(nowInput.getTime()) : new Date(nowInput);
  const safeNow = Number.isFinite(now.getTime()) ? now : new Date();

  switch (normalizedPeriod) {
    case PERIOD_COLUMN_PERIODS.MONTH: {
      const currentMonth = startOfUtcMonth(safeNow);
      return Array.from({ length: 12 }, (_, index) => addUtcMonths(currentMonth, index - 11));
    }
    case PERIOD_COLUMN_PERIODS.WEEK: {
      const monthStart = startOfUtcMonth(safeNow);
      const weekCount = Math.floor((safeNow.getUTCDate() - 1) / 7) + 1;
      return Array.from({ length: weekCount }, (_, index) => addUtcDays(monthStart, index * 7));
    }
    case PERIOD_COLUMN_PERIODS.DAY:
    default: {
      const todayStart = startOfUtcDay(safeNow);
      return Array.from({ length: 31 }, (_, index) => addUtcDays(todayStart, index - 30));
    }
  }
};

export const getPeriodColumnQueryRange = (period, nowInput = new Date()) => {
  const normalizedPeriod = normalizePeriodColumnPeriod(period);
  const now = nowInput instanceof Date ? new Date(nowInput.getTime()) : new Date(nowInput);
  const safeNow = Number.isFinite(now.getTime()) ? now : new Date();

  let rangeStart;

  switch (normalizedPeriod) {
    case PERIOD_COLUMN_PERIODS.WEEK:
      rangeStart = startOfUtcMonth(safeNow);
      break;
    case PERIOD_COLUMN_PERIODS.MONTH:
      rangeStart = new Date(Date.UTC(
        safeNow.getUTCFullYear(),
        safeNow.getUTCMonth() - 11,
        1,
        0,
        0,
        0,
        0,
      ));
      break;
    case PERIOD_COLUMN_PERIODS.DAY:
    default:
      rangeStart = startOfUtcDay(safeNow);
      rangeStart.setUTCDate(rangeStart.getUTCDate() - 30);
      break;
  }

  return {
    tstart: toUnixSeconds(rangeStart),
    tstop: toUnixSeconds(safeNow),
    rangeWindowSec: Math.max(0, toUnixSeconds(safeNow) - toUnixSeconds(rangeStart)),
  };
};

const getPeriodBucketStartIso = (value, period) => {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;

  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  let bucketTimestamp;

  switch (period) {
    case PERIOD_COLUMN_PERIODS.MONTH:
      bucketTimestamp = Date.UTC(year, month, 1, 0, 0, 0, 0);
      break;
    case PERIOD_COLUMN_PERIODS.WEEK: {
      const weekStart = startOfUtcWeekOfMonth(date);
      bucketTimestamp = Date.UTC(
        weekStart.getUTCFullYear(),
        weekStart.getUTCMonth(),
        weekStart.getUTCDate(),
        0,
        0,
        0,
        0,
      );
      break;
    }
    case PERIOD_COLUMN_PERIODS.DAY:
    default:
      bucketTimestamp = Date.UTC(year, month, day, 0, 0, 0, 0);
      break;
  }

  return new Date(bucketTimestamp).toISOString();
};

export const aggregatePeriodColumnSeries = ({
  timestamps = [],
  values = [],
  period = 'day',
  stat = 'max',
  nowInput = new Date(),
} = {}) => {
  const normalizedPeriod = normalizePeriodColumnPeriod(period);
  const normalizedStat = normalizePeriodColumnStat(stat);
  const buckets = new Map();

  timestamps.forEach((timestamp, index) => {
    const bucketKey = getPeriodBucketStartIso(timestamp, normalizedPeriod);
    const numericValue = Number(values[index]);

    if (!bucketKey || !Number.isFinite(numericValue)) {
      return;
    }

    const currentValue = buckets.get(bucketKey);
    if (!Number.isFinite(currentValue)) {
      buckets.set(bucketKey, numericValue);
      return;
    }

    buckets.set(
      bucketKey,
      normalizedStat === 'min'
        ? Math.min(currentValue, numericValue)
        : Math.max(currentValue, numericValue)
    );
  });

  const expectedKeys = getExpectedPeriodBucketStarts(normalizedPeriod, nowInput)
    .map((item) => item.toISOString());
  const fallbackKeys = Array.from(buckets.keys()).sort((left, right) => Date.parse(left) - Date.parse(right));
  const sortedKeys = expectedKeys.length > 0 ? expectedKeys : fallbackKeys;

  return {
    xData: sortedKeys,
    yData: sortedKeys.map((key) => (buckets.has(key) ? buckets.get(key) : null)),
  };
};

export const isNotFoundApiError = (error) => Number(error?.response?.status) === 404;

export const resolvePeriodColumnWidgetBinding = ({
  widget,
  datastreams = [],
  devices = [],
} = {}) => {
  const allDatastreams = Array.isArray(datastreams) ? datastreams.filter(Boolean) : [];
  const allDevices = Array.isArray(devices) ? devices.filter(Boolean) : [];
  const widgetDeviceId = Number(widget?.device_id ?? widget?.deviceId);
  const widgetDataId = Number(widget?.data_id ?? widget?.dataId);

  const device = Number.isFinite(widgetDeviceId) && widgetDeviceId > 0
    ? allDevices.find((item) => Number(item?.id) === widgetDeviceId) || null
    : null;

  const scopedDatastreams = Array.isArray(device?.template?.datastreams)
    ? device.template.datastreams.filter(Boolean)
    : allDatastreams;

  const datastream = Number.isFinite(widgetDataId) && widgetDataId > 0
    ? scopedDatastreams.find((item) => Number(item?.id) === widgetDataId) || null
    : null;

  const pin = String(datastream?.pin || widget?.extra_props?.pin || widget?.extraProps?.pin || '').trim();

  return {
    device,
    datastream,
    pin,
  };
};
